/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

// Simple hash function for creating compact cache keys
export const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `${Math.abs(hash).toString(36)}`;
};

export const getCacheKey = (query: TypedDocumentNode, vars?: any): string => {
  // Get query document string for hashing
  const queryStr = query.loc?.source.body ?? JSON.stringify(query.definitions);
  const queryHash = simpleHash(queryStr);

  // Extract operation name for readability
  const definition = query.definitions?.[0];
  const operationName =
    definition?.kind === "OperationDefinition"
      ? definition.name?.value
      : "unknown";

  // Create query key with hash to prevent collisions between different query documents
  const queryKey = `${operationName}:${queryHash}`;

  if (!vars || Object.keys(vars).length === 0) {
    console.warn(
      "[getCacheKey] Empty or missing vars for operation:",
      queryKey,
    );
    return queryKey;
  }

  // Use a replacer function to recursively sort all nested object keys
  // This ensures identical data with different property orders produces same cache key
  const stableVars = JSON.stringify(vars, (_key, value) => {
    // Recursively sort keys for all objects (not arrays, not null)
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const sorted: any = {};
      Object.keys(value)
        .sort()
        .forEach((k) => {
          sorted[k] = value[k];
        });
      return sorted;
    }
    return value;
  });

  if (stableVars === "{}") {
    console.warn(
      "[getCacheKey] Variables object is empty after stringify, operation:",
      queryKey,
      "vars:",
      vars,
    );
    return queryKey;
  }

  // Hash the variables to keep cache key compact
  const varsHash = simpleHash(stableVars);

  return `${queryKey}|${varsHash}`;
};
